'use client'

import * as React from 'react'
import { Link } from 'react-router-dom'
import { Mail, Lock, Eye, EyeOff, User, Building2 } from 'lucide-react'
import { Button } from 'components/ui/button'
import { Input } from 'components/ui/input'
import { Label } from 'components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { toast } from 'sonner'

export default function RegisterPage() {
  const [showPassword, setShowPassword] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)

  function handleSubmit(e) {
    e.preventDefault()
    setIsLoading(true)
    setTimeout(() => {
      setIsLoading(false)
      toast.success('Access request submitted. An administrator will review your request.')
    }, 1500)
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3 lg:hidden">
        <img src="/jooust/logo.jpg" alt="JOOUST Logo" className="size-10 rounded-lg object-cover" />
        <div>
          <h1 className="text-base font-bold text-foreground font-serif">JOOUST PMIS</h1>
          <p className="text-[10px] text-muted-foreground">Project Management & MEL</p>
        </div>
      </div>

      <Card className="border-0 shadow-none lg:border lg:shadow-sm">
        <CardHeader className="px-0 lg:px-6">
          <CardTitle className="text-2xl font-serif font-bold text-foreground">
            Request access
          </CardTitle>
          <CardDescription className="text-sm text-muted-foreground">
            Submit your details to request a PMIS account
          </CardDescription>
        </CardHeader>
        <CardContent className="px-0 lg:px-6">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="name" className="text-sm font-medium text-foreground">Full name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="name" placeholder="Dr. John Doe" className="pl-10 h-10" required />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="email" className="text-sm font-medium text-foreground">Email address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input id="email" type="email" placeholder="name@jooust.ac.ke" className="pl-10 h-10" required />
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="unit" className="text-sm font-medium text-foreground">Institutional unit</Label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground z-10" />
                <Select>
                  <SelectTrigger className="pl-10 h-10">
                    <SelectValue placeholder="Select your department" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="research">Research & Innovation Directorate</SelectItem>
                    <SelectItem value="agriculture">Faculty of Agriculture</SelectItem>
                    <SelectItem value="science">Faculty of Science</SelectItem>
                    <SelectItem value="health">Faculty of Health Sciences</SelectItem>
                    <SelectItem value="ict">Faculty of Information Science & Technology</SelectItem>
                    <SelectItem value="bio">Faculty of Biological Sciences</SelectItem>
                    <SelectItem value="finance">Finance Department</SelectItem>
                    <SelectItem value="mel">M&E Unit</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="role" className="text-sm font-medium text-foreground">Requested role</Label>
              <Select>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Select your role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pi">Principal Investigator</SelectItem>
                  <SelectItem value="project_officer">Project Officer</SelectItem>
                  <SelectItem value="mel_officer">M&E Officer</SelectItem>
                  <SelectItem value="finance_liaison">Finance Liaison</SelectItem>
                  <SelectItem value="partner">Partner / Sub-grantee</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="password" className="text-sm font-medium text-foreground">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Min 12 chars, mixed case, numbers, symbols"
                  className="pl-10 pr-10 h-10"
                  required
                  minLength={12}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground">
                Must be at least 12 characters with mixed case, numbers, and symbols
              </p>
            </div>

            <Button
              type="submit"
              className="mt-2 h-10 w-full bg-primary text-primary-foreground hover:bg-accent font-medium"
              disabled={isLoading}
            >
              {isLoading ? 'Submitting...' : 'Request access'}
            </Button>
          </form>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </CardContent>
      </Card>
    </div>
  )
}